import { ThemeToggle } from '../components/ThemeToggle';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, REQUISITE_TYPE_OPTIONS, RequisiteType } from '../lib/api';
import { cabinetPath } from '../lib/domains';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import {
    ArrowLeft,
    TrendingUp,
    RefreshCw,
    ChevronDown,
    ChevronRight,
    ArrowDownCircle,
    ArrowUpCircle,
    Calendar,
} from 'lucide-react';

// ─── Типы ────────────────────────────────────────────────────────────────────

interface MerchantStats {
    merchant_id: string;
    merchant_name: string;
    requests: number;
    issued: number;
    unique_issued?: number;
    successes: number;
    in_usdt: number;
    out_usdt: number;
    in_rub: number;
    out_rub: number;
    pct_issued: number;
    pct_unique_issued?: number;
    pct_success: number;
    margin_usdt: number | null;
    marginality_pct: number | null;
    rate_label: string;
    avg_rate: number | null;
    avg_check_rub: number;
    fiat_currency?: string;
    in_fiat?: number;
    out_fiat?: number;
    avg_check_fiat?: number;
}

interface DayTotal {
    requests: number;
    issued: number;
    unique_issued?: number;
    successes: number;
    in_usdt: number;
    out_usdt: number;
    in_rub: number;
    out_rub: number;
    pct_issued: number;
    pct_unique_issued?: number;
    pct_success: number;
    margin_usdt: number | null;
    marginality_pct: number | null;
    rate_label: string;
    avg_rate: number | null;
    avg_check_rub: number;
    fiat_totals?: Record<string, { in: number; out: number }>;
    fiat_rates?: Record<string, { rate_label: string; avg_rate: number | null }>;
}

interface ConversionDayStats {
    date: string;
    total: DayTotal;
    merchants: MerchantStats[];
}

// ─── Вспомогательные компоненты ──────────────────────────────────────────────

/** Цветовая индикация процента */
const PctBadge: React.FC<{ value: number }> = ({ value }) => {
    let colorClass = 'text-red-500 font-semibold';
    if (value >= 60) colorClass = 'text-green-600 dark:text-green-300 font-semibold';
    else if (value >= 30) colorClass = 'text-yellow-500 font-semibold';
    return <span className={colorClass}>{value.toFixed(2)}%</span>;
};

/** Форматирование числа с разделителями */
const fmt = (n: number | null, decimals = 2) =>
    n == null ? '—' : n.toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

/** Форматирование даты */
const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
};

/** Не показывать служебный текст для отсутствующего курса. */
const visibleRateLabel = (label?: string | null) =>
    label && label !== 'Курс не указан' ? label : '';

// ─── Строка мерчанта ──────────────────────────────────────────────────────────

const MerchantRow: React.FC<{ m: MerchantStats; paymentType: string }> = ({ m, paymentType }) => {
    const issued = m.unique_issued ?? m.issued;
    const pctIssued = m.pct_unique_issued ?? m.pct_issued;
    const fiatCurrency = m.fiat_currency || 'RUB';
    const inFiat = m.in_fiat ?? m.in_rub;
    const outFiat = m.out_fiat ?? m.out_rub;

    return (
        <tr className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900 text-sm">
            <td className="py-2 pl-10 pr-3 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                <span>{m.merchant_name}</span>{' '}
                <Badge variant="outline" className="text-[10px]">{fiatCurrency}</Badge>
            </td>
            {paymentType === 'all' && (
                <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200 whitespace-nowrap">
                    <div className="text-xs">{fmt(m.in_usdt, 4)}</div>
                    <div className="text-xs text-slate-400">{fmt(m.out_usdt, 4)}</div>
                </td>
            )}
            {paymentType === 'all' && (
                <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200 whitespace-nowrap">
                    <div className="text-xs">{fmt(inFiat)} {fiatCurrency}</div>
                    <div className="text-xs text-slate-400">{fmt(outFiat)} {fiatCurrency}</div>
                </td>
            )}
            {paymentType === 'deposit' && (
                <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200 whitespace-nowrap">
                    {fmt(m.in_usdt, 4)}
                </td>
            )}
            {paymentType === 'deposit' && (
                <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200 whitespace-nowrap">
                    {fmt(inFiat)} {fiatCurrency}
                </td>
            )}
            {paymentType === 'withdraw' && (
                <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200 whitespace-nowrap">
                    {fmt(m.out_usdt, 4)}
                </td>
            )}
            {paymentType === 'withdraw' && (
                <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200 whitespace-nowrap">
                    {fmt(outFiat)} {fiatCurrency}
                </td>
            )}
            <td className="py-2 px-3 text-right whitespace-nowrap">
                <PctBadge value={pctIssued} />
            </td>
            <td className="py-2 px-3 text-right whitespace-nowrap">
                <PctBadge value={m.pct_success} />
            </td>
            <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200">{m.requests}</td>
            <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200">{issued}</td>
            <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200">{m.successes}</td>
            <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200 whitespace-nowrap">{fmt(m.margin_usdt, 4)}</td>
            <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200 whitespace-nowrap">{m.marginality_pct == null ? '—' : `${fmt(m.marginality_pct)}%`}</td>
            <td className="py-2 px-3 text-right text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">
                {visibleRateLabel(m.rate_label)}
            </td>
            <td className="py-2 px-3 text-right text-slate-700 dark:text-slate-200 whitespace-nowrap">{fmt(m.avg_check_fiat ?? m.avg_check_rub)} {fiatCurrency}</td>
        </tr>
    );
};

// ─── Строка дня ───────────────────────────────────────────────────────────────

const DayRow: React.FC<{ day: ConversionDayStats; paymentType: string }> = ({ day, paymentType }) => {
    const [expanded, setExpanded] = useState(true);
    const t = day.total;
    const issued = t.unique_issued ?? t.issued;
    const pctIssued = t.pct_unique_issued ?? t.pct_issued;
    const fiatTotals = t.fiat_totals || { RUB: { in: t.in_rub, out: t.out_rub } };
    const fiatEntries = Object.entries(fiatTotals);

    return (
        <>
            {/* Заголовок дня */}
            <tr
                className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                onClick={() => setExpanded(e => !e)}
            >
                <td className="py-3 px-3 font-bold text-slate-800 dark:text-slate-100 whitespace-nowrap" colSpan={1}>
                    <div className="flex items-center gap-2">
                        {expanded
                            ? <ChevronDown className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                            : <ChevronRight className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                        }
                        <span>{formatDate(day.date)}</span>
                        <Badge variant="outline" className="text-xs font-normal ml-1">
                            {day.merchants.length} мерч.
                        </Badge>
                    </div>
                </td>
                {paymentType === 'all' && (
                    <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">
                        <div>{fmt(t.in_usdt, 4)}</div>
                        <div className="text-slate-500 dark:text-slate-400 text-xs font-normal">{fmt(t.out_usdt, 4)}</div>
                    </td>
                )}
                {paymentType === 'all' && (
                    <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">
                        {fiatEntries.map(([currency, values]) => (
                            <div key={currency} className="text-xs">
                                {fmt(values.in)} / {fmt(values.out)} {currency}
                            </div>
                        ))}
                    </td>
                )}
                {paymentType === 'deposit' && (
                    <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">
                        {fmt(t.in_usdt, 4)}
                    </td>
                )}
                {paymentType === 'deposit' && (
                    <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">
                        {fiatEntries.map(([currency, values]) => <div key={currency}>{fmt(values.in)} {currency}</div>)}
                    </td>
                )}
                {paymentType === 'withdraw' && (
                    <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">
                        {fmt(t.out_usdt, 4)}
                    </td>
                )}
                {paymentType === 'withdraw' && (
                    <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">
                        {fiatEntries.map(([currency, values]) => <div key={currency}>{fmt(values.out)} {currency}</div>)}
                    </td>
                )}
                <td className="py-3 px-3 text-right whitespace-nowrap">
                    <PctBadge value={pctIssued} />
                </td>
                <td className="py-3 px-3 text-right whitespace-nowrap">
                    <PctBadge value={t.pct_success} />
                </td>
                <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100">{t.requests}</td>
                <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100">{issued}</td>
                <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100">{t.successes}</td>
                <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">{fmt(t.margin_usdt, 4)}</td>
                <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">{t.marginality_pct == null ? '—' : `${fmt(t.marginality_pct)}%`}</td>
                <td className="py-3 px-3 text-right text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">
                    {t.fiat_rates
                        ? fiatEntries.map(([currency]) => {
                            const rateLabel = visibleRateLabel(t.fiat_rates?.[currency]?.rate_label);
                            return rateLabel ? <div key={currency}>{rateLabel} {currency}</div> : null;
                        })
                        : visibleRateLabel(t.rate_label)
                    }
                </td>
                <td className="py-3 px-3 text-right font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">{fmt(t.avg_check_rub)} RUB</td>
            </tr>

            {/* Строки мерчантов */}
            {expanded && day.merchants.map(m => (
                <MerchantRow key={`${m.merchant_id}:${m.fiat_currency || 'RUB'}`} m={m} paymentType={paymentType} />
            ))}
        </>
    );
};

// ─── Главная страница ─────────────────────────────────────────────────────────

export const AdminConversionPage: React.FC = () => {
    const navigate = useNavigate();
    const [conversionSource, setConversionSource] = useState<'daily' | 'legacy'>('daily');

    // Фильтры
    const [paymentType, setPaymentType] = useState<string>('all');
    const [requisiteType, setRequisiteType] = useState<RequisiteType | 'all'>('all');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    // Данные
    const [views, setViews] = useState<Record<'daily' | 'legacy', {
        data: ConversionDayStats[];
        pagination: { page: number; total: number; pages: number };
        loading: boolean;
        error: string | null;
    }>>({
        daily: { data: [], pagination: { page: 1, total: 0, pages: 1 }, loading: false, error: null },
        legacy: { data: [], pagination: { page: 1, total: 0, pages: 1 }, loading: false, error: null },
    });
    const PAGE_SIZE = 10;
    const { data, pagination, loading, error } = views[conversionSource];

    const loadData = useCallback(async (source: 'daily' | 'legacy', page: number) => {
        setViews(prev => ({
            ...prev,
            [source]: { ...prev[source], loading: true, error: null },
        }));
        try {
            const request = source === 'daily'
                ? adminAPI.getDailyConversionStats
                : adminAPI.getConversionStats;
            const res = await request({
                page,
                size: PAGE_SIZE,
                paymentType: paymentType !== 'all' ? paymentType : undefined,
                requisiteType: requisiteType !== 'all' ? requisiteType : undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
            });
            setViews(prev => ({
                ...prev,
                [source]: {
                    data: res.items as ConversionDayStats[],
                    pagination: { page: res.page, total: res.total, pages: res.pages },
                    loading: false,
                    error: null,
                },
            }));
        } catch (e: any) {
            setViews(prev => ({
                ...prev,
                [source]: { ...prev[source], loading: false, error: 'Ошибка загрузки данных' },
            }));
            console.error(e);
        }
    }, [paymentType, requisiteType, startDate, endDate]);

    useEffect(() => {
        loadData(conversionSource, 1);
    }, [conversionSource, loadData]);

    const handlePageChange = (page: number) => {
        loadData(conversionSource, page);
    };

    const handleResetFilters = () => {
        setPaymentType('all');
        setRequisiteType('all');
        setStartDate('');
        setEndDate('');
    };

    const hasActiveFilters = paymentType !== 'all' || requisiteType !== 'all' || startDate || endDate;

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 md:p-8">
            {/* Шапка */}
            <header className="flex flex-wrap items-center gap-4 mb-6">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(cabinetPath('admin', '/dashboard'))}
                    className="flex items-center gap-2"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Назад
                </Button>
                <div className="flex items-center gap-3">
                    <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-300" />
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Конверсия</h1>
                    </div>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadData(conversionSource, pagination.page)}
                    disabled={loading}
                    className="ml-auto flex items-center gap-2"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Обновить
                </Button>
                <ThemeToggle />
            </header>

            <div className="mb-6 inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-1 shadow-sm">
                <Button
                    size="sm"
                    variant={conversionSource === 'daily' ? 'default' : 'ghost'}
                    onClick={() => setConversionSource('daily')}
                >
                    Конверсия (новая)
                </Button>
                <Button
                    size="sm"
                    variant={conversionSource === 'legacy' ? 'default' : 'ghost'}
                    onClick={() => setConversionSource('legacy')}
                >
                    Конверсия (старая)
                </Button>
            </div>

            {/* Фильтры */}
            <Card className="mb-6">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-slate-700 dark:text-slate-200">Фильтры</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Тип платежа */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                                Тип платежа
                            </label>
                            <div className="flex gap-1">
                                <Button
                                    size="sm"
                                    variant={paymentType === 'all' ? 'default' : 'outline'}
                                    onClick={() => setPaymentType('all')}
                                    className="text-xs"
                                >
                                    Все
                                </Button>
                                <Button
                                    size="sm"
                                    variant={paymentType === 'deposit' ? 'default' : 'outline'}
                                    onClick={() => setPaymentType('deposit')}
                                    className={`text-xs flex items-center gap-1 ${paymentType === 'deposit' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                >
                                    <ArrowDownCircle className="h-3 w-3" />
                                    Pay-in
                                </Button>
                                <Button
                                    size="sm"
                                    variant={paymentType === 'withdraw' ? 'default' : 'outline'}
                                    onClick={() => setPaymentType('withdraw')}
                                    className={`text-xs flex items-center gap-1 ${paymentType === 'withdraw' ? 'bg-orange-600 hover:bg-orange-700' : ''}`}
                                >
                                    <ArrowUpCircle className="h-3 w-3" />
                                    Pay-out
                                </Button>
                            </div>
                        </div>

                        {/* Метод оплаты */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                                Метод оплаты
                            </label>
                            <select
                                value={requisiteType}
                                onChange={(e) => setRequisiteType(e.target.value as RequisiteType | 'all')}
                                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            >
                                <option value="all">Все методы</option>
                                {REQUISITE_TYPE_OPTIONS.map(pm => (
                                    <option key={pm.value} value={pm.value}>
                                        {pm.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Временной диапазон */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Временной диапазон
                            </label>
                            <div className="flex gap-2 items-center">
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-xs text-slate-400">Начало периода</span>
                                    <input
                                        type="datetime-local"
                                        value={startDate}
                                        onChange={e => setStartDate(e.target.value)}
                                        className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    />
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-xs text-slate-400">Конец периода</span>
                                    <input
                                        type="datetime-local"
                                        value={endDate}
                                        onChange={e => setEndDate(e.target.value)}
                                        className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Сброс фильтров */}
                        {hasActiveFilters && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleResetFilters}
                                className="text-slate-500 dark:text-slate-400 self-end"
                            >
                                Сбросить фильтры
                            </Button>
                        )}
                    </div>

                    {/* Легенда цветов */}
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                        <span className="font-medium">Цвет %:</span>
                        <span className="text-red-500 font-semibold">0–29% (низкий)</span>
                        <span className="text-yellow-500 font-semibold">30–59% (средний)</span>
                        <span className="text-green-600 dark:text-green-300 font-semibold">60–100% (высокий)</span>
                    </div>
                </CardContent>
            </Card>

            {/* Таблица */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <RefreshCw className="h-6 w-6 animate-spin text-slate-400 mr-3" />
                            <span className="text-slate-500 dark:text-slate-400">Загрузка данных...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-16 text-red-500">
                            <p>{error}</p>
                            <Button variant="outline" size="sm" onClick={() => loadData(conversionSource, 1)} className="mt-3">
                                Повторить
                            </Button>
                        </div>
                    ) : data.length === 0 ? (
                        <div className="text-center py-16 text-slate-400">
                            <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>
                                {conversionSource === 'daily'
                                    ? 'Нет данных за выбранный период (новая конверсия)'
                                    : 'Нет данных за выбранный период'}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                                        <th className="py-3 px-3 text-left font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap min-w-[160px]">
                                            Дата / Мерчант
                                        </th>
                                        {paymentType === 'all' && (
                                            <>
                                                <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                                    Вход / Выход<br />
                                                    <span className="text-xs font-normal text-slate-400">(USDT)</span>
                                                </th>
                                                <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                                    Вход / Выход<br />
                                                    <span className="text-xs font-normal text-slate-400">(Fiat, отдельно по валютам)</span>
                                                </th>
                                            </>
                                        )}
                                        {paymentType === 'deposit' && (
                                            <>
                                                <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                                    Вход<br />
                                                    <span className="text-xs font-normal text-slate-400">(USDT)</span>
                                                </th>
                                                <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                                    Вход<br />
                                                    <span className="text-xs font-normal text-slate-400">(Fiat)</span>
                                                </th>
                                            </>
                                        )}
                                        {paymentType === 'withdraw' && (
                                            <>
                                                <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                                    Выход<br />
                                                    <span className="text-xs font-normal text-slate-400">(USDT)</span>
                                                </th>
                                                <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                                    Выход<br />
                                                    <span className="text-xs font-normal text-slate-400">(Fiat)</span>
                                                </th>
                                            </>
                                        )}
                                        <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                            % выдач
                                        </th>
                                        <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                            % успеха
                                        </th>
                                        <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                            Запросов
                                        </th>
                                        <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                            Выдач
                                        </th>
                                        <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                            Успехов
                                        </th>
                                        <th title="Прибыль завершённых сделок с учётом комиссий и курсов. Прочерк — недостаточно данных." className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                            Маржа<br />
                                            <span className="text-xs font-normal text-slate-400">(USDT)</span>
                                        </th>
                                        <th title="Прибыль / (вход + выход в USDT) × 100. Прочерк — недостаточно данных." className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                            Маржи-<br />нальность
                                        </th>
                                        <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                            Курс
                                        </th>
                                        <th className="py-3 px-3 text-right font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                            Средний чек<br />
                                            <span className="text-xs font-normal text-slate-400">(fiat строки)</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map(day => (
                                        <DayRow key={day.date} day={day} paymentType={paymentType} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Пагинация */}
                    {!loading && !error && pagination.pages > 1 && (
                        <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-4">
                            <Pagination
                                currentPage={pagination.page}
                                totalPages={pagination.pages}
                                onPageChange={handlePageChange}
                                totalItems={pagination.total}
                                pageSize={PAGE_SIZE}
                            />
                        </div>
                    )}

                    {/* Итого */}
                    {!loading && !error && data.length > 0 && (
                        <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-3 bg-slate-50 dark:bg-slate-900 text-xs text-slate-500 dark:text-slate-400 flex items-center justify-between">
                            <span>
                                Показано дней: <strong>{data.length}</strong> из <strong>{pagination.total}</strong>
                            </span>
                            <span>
                                Страница <strong>{pagination.page}</strong> из <strong>{pagination.pages}</strong>
                                {' '}(по {PAGE_SIZE} дней)
                            </span>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
